import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { formSchemaApis } from "../../../api/formSchemaApis";
import { submissionApis } from "../../../api/submissionApis";
import { reportEndpoints } from "../../../api/endpoints/report.endpoints";
import debounce from "lodash.debounce";
import Loader from "../../Loader";
import AssignButton from "../reports/rejected/AssignButton";
import AssignModal from "./assignments/AssignModal";
import { useTaggingStore } from "./assignments/chatStore";
import useFetch from "../../../utils/hooks/useFetch";
import {
  normalizeMemberId,
  resolveLegacyKpiAssignCode,
} from "../../../utils/hydrateReportFromDynamicKpis";
import constant from "../../../constant";
import emptyuser from "../../../assets/emptyuser.avif";
import messages from "../../../assets/messages.svg";
import "./dataPoints.css";

const SECTION_TO_KPI_NAME = {
  Base: "basicSectorSpecificKPI",
  Environment: "environmentKPI",
  Social: "socialKPI",
  Governance: "governanceKPI",
  "Governance & Economics": "governanceKPI",
  Economics: "governanceKPI",
  Dynamic: "basicSectorSpecificKPI",
};

// Section values that map to the standard tabs (for filtering "other" schemas)
const STANDARD_SECTIONS = ["Base", "Environment", "Social", "Governance", "Governance & Economics", "Economics"];

const normalizeSection = (s) => (s || "").trim().toLowerCase();
const isGovernanceSection = (s) => {
  const n = normalizeSection(s);
  return n === "governance" || n === "governance & economics" || n === "economics";
};
const matchesSection = (schemaSection, filterSection) => {
  const schemaNorm = normalizeSection(schemaSection);
  const filterNorm = normalizeSection(filterSection);
  if (!filterNorm) return true;
  if (filterNorm === "dynamic") {
    const isStandard = STANDARD_SECTIONS.some(
      (std) => normalizeSection(std) === schemaNorm
    ) || isGovernanceSection(schemaSection);
    return !isStandard;
  }
  if (filterNorm === "governance") return isGovernanceSection(schemaSection);
  return schemaNorm === filterNorm;
};

const isLegacySectionCode = (activeSection, code) => {
  const match = String(code || "").match(/^([BESG])(\d+)$/);
  if (!match) return false;
  const prefix = match[1];
  const index = Number(match[2]);

  const sectionNorm = normalizeSection(activeSection);
  if (sectionNorm === "base") return prefix === "B" && index >= 1 && index <= 6;
  if (sectionNorm === "environment") return prefix === "E" && index >= 1 && index <= 9;
  if (sectionNorm === "social") return prefix === "S" && index >= 1 && index <= 8;
  if (isGovernanceSection(activeSection)) return prefix === "G" && index >= 1 && index <= 10;
  return false;
};

const LEGACY_KEY_ALIASES = {
  B1: { scope1: "scope1Emissions", scope2: "scope2Emissions" },
};

const normalizeLegacyInputKey = (code, inputKey) => {
  const codeMap = LEGACY_KEY_ALIASES[String(code || "")];
  if (!codeMap) return inputKey;
  return codeMap[inputKey] || inputKey;
};

const getSchemaId = (schema) =>
  String(schema?.id || schema?._id || "");

const buildSchemaValuesFromFormData = (schema, currentFormData) => {
  const values = {};
  const schemaId = getSchemaId(schema);
  (schema?.inputs || []).forEach((input) => {
    const inputKey = String(input?.key || "");
    if (!inputKey) return;
    const fieldKey = `${schemaId}_${inputKey}`;
    const raw = currentFormData[fieldKey];
    if (input.type === "number") {
      if (raw === "" || raw === null || raw === undefined) values[inputKey] = null;
      else values[inputKey] = Number(String(raw).replace(/,/g, ""));
    } else if (input.type === "boolean") {
      values[inputKey] = raw === true || raw === "true";
    } else {
      values[inputKey] = raw ?? "";
    }
  });
  return values;
};

const DynamicKpiForm = ({ sector, section, refreshKey = 0 }) => {
  const params = useParams();
  const [schemas, setSchemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState({});
  const [hasExistingSubmissions, setHasExistingSubmissions] = useState({});
  const reportId = params.id;

  const { data: reportData, refetch: refetchReport } = useFetch(
    reportEndpoints.getReport(params.id)
  );
  const { allUsers, fetchUsers } = useTaggingStore();

  const [modalState, setModalState] = useState({
    visible: false,
    top: 0,
    left: 0,
    symbol: null,
    code: "",
    kpiName: "",
    alreadyTagged: [],
  });

  const getKpiNameForSection = () =>
    SECTION_TO_KPI_NAME[section] || "basicSectorSpecificKPI";

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const getAssignContext = (schema) => {
    const legacyCode = resolveLegacyKpiAssignCode(schema);
    const kpiName = getKpiNameForSection();
    const kpiSection = legacyCode ? reportData?.report?.[kpiName]?.[legacyCode] : null;
    const assignMembers = (kpiSection?.assign?.assignMembers || [])
      .map(normalizeMemberId)
      .filter(Boolean);

    return { legacyCode, kpiName, kpiSection, assignMembers };
  };

  const handleAssignClick = (event, schema, alreadyTagged) => {
    const { legacyCode } = getAssignContext(schema);
    if (!legacyCode) return;

    const buttonRect = event.target.getBoundingClientRect();
    const maxModalHeight = 550;
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;
    let topPosition;
    let modalHeight;
    if (spaceBelow >= maxModalHeight) {
      topPosition = buttonRect.bottom;
      modalHeight = maxModalHeight;
    } else if (spaceAbove >= maxModalHeight) {
      topPosition = buttonRect.top - maxModalHeight;
      modalHeight = maxModalHeight;
    } else if (spaceBelow > spaceAbove) {
      topPosition = buttonRect.bottom;
      modalHeight = spaceBelow;
    } else {
      topPosition = buttonRect.top - spaceAbove;
      modalHeight = spaceAbove;
    }
    const modalWidth = 500;
    const leftPosition = Math.min(
      buttonRect.right - modalWidth,
      window.innerWidth - modalWidth
    );
    setModalState({
      visible: true,
      top: topPosition,
      left: leftPosition,
      symbol: schema.code,
      height: modalHeight,
      code: legacyCode,
      kpiName: getKpiNameForSection(),
      alreadyTagged,
    });
  };

  const handleAssignmentChange = () => {
    refetchReport();
  };

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, visible: false }));
  };

  useEffect(() => {
    if (sector) {
      loadSchemasAndSubmissions(sector);
    }
  }, [sector, section, reportId, refreshKey]);

  const loadSchemasAndSubmissions = async (sectorName) => {
    try {
      setLoading(true);
      
      // Get schemas from API - the endpoint returns array directly
      const schemasData = await formSchemaApis.getSchemasBySector(sectorName);
      let schemasArray = Array.isArray(schemasData) ? schemasData : (schemasData?.items || schemasData?.data || []);
      schemasArray = schemasArray
        .map((schema) => ({
          ...schema,
          id: getSchemaId(schema),
          inputs: Array.isArray(schema?.inputs) ? schema.inputs : [],
        }))
        .filter((schema) => schema.id);
      // Filter by section if provided
      if (section != null) {
        schemasArray = schemasArray.filter((s) => matchesSection(s.section, section));
      }
      setSchemas(schemasArray);
      
      // Initialize form data for all inputs with empty values
      const initialData = {};
      schemasArray.forEach((schema) => {
        schema.inputs?.forEach((input) => {
          initialData[`${schema.id}_${input.key}`] = "";
        });
      });

      // Load latest submissions by sector/schema for dynamic KPI form hydration.
      if (reportId) {
        try {
          const existingSubmissions = await submissionApis.getSubmissionsByReport(reportId);
          const schemaIds = new Set(schemasArray.map((s) => String(s.id)));
          const filteredBySchema = existingSubmissions.filter((s) => {
            const sid = typeof s.schema === "object" ? (s.schema?.id || s.schema?._id) : s.schema;
            return schemaIds.has(String(sid));
          });
          // Keep latest submission per schema
          const latestBySchema = {};
          filteredBySchema.forEach((submission) => {
            const sid = typeof submission.schema === "object" ? (submission.schema?.id || submission.schema?._id) : submission.schema;
            const key = String(sid);
            if (!latestBySchema[key] || new Date(submission.createdAt) > new Date(latestBySchema[key].createdAt)) {
              latestBySchema[key] = submission;
            }
          });
          const submissionsToApply = Object.values(latestBySchema);
          
          // Populate form data with existing submission values
          submissionsToApply.forEach((submission) => {
            // Extract schema ID - handle both populated object and direct ID
            let schemaId;
            if (typeof submission.schema === 'object' && submission.schema !== null) {
              schemaId = submission.schema.id || submission.schema._id || submission.schema.id?.toString() || submission.schema._id?.toString();
            } else {
              schemaId = submission.schema?.toString();
            }
            
            if (submission.values && schemaId) {
              Object.keys(submission.values).forEach((inputKey) => {
                const fieldKey = `${schemaId}_${inputKey}`;
                let value = submission.values[inputKey];
                
                // Convert value to string for form inputs
                if (value === null || value === undefined) {
                  value = "";
                } else if (typeof value === "boolean") {
                  value = value.toString();
                } else {
                  value = String(value);
                }
                
                initialData[fieldKey] = value;
              });
              
              // Store calculated values for display
              if (submission.calculated) {
                setSubmitted((prev) => ({
                  ...prev,
                  [schemaId]: {
                    calculated: submission.calculated
                  }
                }));
                // Mark that this schema has existing data
                setHasExistingSubmissions((prev) => ({
                  ...prev,
                  [schemaId]: true
                }));
              }
            }
          });
        } catch (error) {
          console.error("Error loading existing submissions:", error);
          // Continue even if submissions fail to load
        }
      }
      
      setFormData(initialData);
    } catch (error) {
      console.error("Error loading schemas:", error);
      // Set empty array on error to show no data message
      setSchemas([]);
    } finally {
      setLoading(false);
    }
  };

  // API Call Function for auto-save
  const sendDynamicKPIRequest = ({ schema, values }) => {
    const schemaId = getSchemaId(schema);
    if (!schemaId) return;
    submissionApis
      .submitForSchema(schemaId, {
        values,
        reportId: reportId || null,
        submittedBy: reportId || "user",
        notes: "",
      })
      .then((result) => {
        setSubmitted((prev) => ({
          ...prev,
          [schemaId]: {
            calculated: result?.calculated || null,
          },
        }));
        setHasExistingSubmissions((prev) => ({
          ...prev,
          [schemaId]: true,
        }));
      })
      .catch((error) => console.error("Error saving Dynamic KPI:", error));
  };

  // Debounced save function
  const debouncedSaveDynamicKPI = useRef(debounce(sendDynamicKPIRequest, 500));

  // Flush pending API calls before unmounting
  useEffect(() => {
    return () => {
      debouncedSaveDynamicKPI.current.cancel();
    };
  }, []);

  const handleInputChange = (schemaId, inputKey, value) => {
    const fieldKey = `${schemaId}_${inputKey}`;
    
    // Find the schema to get its code
    const schema = schemas.find((s) => getSchemaId(s) === String(schemaId));
    if (!schema) return;
    const stableSchemaId = getSchemaId(schema);
    
    setFormData((prev) => ({
      ...prev,
      [fieldKey]: value,
    }));
    
    // Clear error for this field
    if (errors[fieldKey]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldKey];
        return newErrors;
      });
    }

    const normalizedInputKey =
      section !== "Dynamic" && isLegacySectionCode(section, schema.code)
        ? normalizeLegacyInputKey(schema.code, inputKey)
        : inputKey;
    const nextFormData = {
      ...formData,
      [fieldKey]: value,
    };
    // Keep alias-normalized shadow value so submission payload uses canonical key.
    if (normalizedInputKey !== inputKey) {
      nextFormData[`${stableSchemaId}_${normalizedInputKey}`] = value;
    }
    const values = buildSchemaValuesFromFormData(schema, nextFormData);

    // Auto-save with debounce
    debouncedSaveDynamicKPI.current({ schema, values });
  };

  const validateForm = (schema) => {
    const schemaErrors = {};
    schema.inputs?.forEach((input) => {
      const fieldKey = `${schema.id}_${input.key}`;
      const value = formData[fieldKey];
      
      if (input.required && (!value || value === "")) {
        schemaErrors[fieldKey] = `${input.label} is required`;
      }
      
      if (input.type === "number" && value && isNaN(parseFloat(value))) {
        schemaErrors[fieldKey] = `${input.label} must be a number`;
      }
    });
    
    return schemaErrors;
  };

  const handleSubmit = async (schema) => {
    const schemaErrors = validateForm(schema);
    
    if (Object.keys(schemaErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...schemaErrors }));
      return;
    }

    try {
      // Prepare values object using input keys
      const values = {};
      schema.inputs?.forEach((input) => {
        const fieldKey = `${schema.id}_${input.key}`;
        let value = formData[fieldKey];
        
        // Convert to appropriate type
        if (input.type === "number") {
          value = value ? parseFloat(value) : null;
        } else if (input.type === "boolean") {
          value = value === "true" || value === true;
        }
        
        values[input.key] = value;
      });

      const submissionData = {
        values,
        reportId: reportId || null,
        submittedBy: params.id || reportId || "user",
        notes: "",
      };

      console.log("Submitting data:", {
        schemaId: schema.id,
        submissionData,
        schema
      });

      const result = await submissionApis.submitForSchema(schema.id, submissionData);
      
      console.log("Submission result:", result);
      
      const hasExisting = submitted[schema.id] || hasExistingSubmissions[schema.id];
      
      setSubmitted((prev) => ({ 
        ...prev, 
        [schema.id]: {
          calculated: result.calculated || result?.calculated
        }
      }));
      
      // Mark that this schema now has submissions
      setHasExistingSubmissions((prev) => ({
        ...prev,
        [schema.id]: true
      }));
      
      // Show success message (you can replace this with a toast notification)
      alert(`Successfully ${hasExisting ? 'updated' : 'submitted'} ${schema.kpi}`);
    } catch (error) {
      console.error("Error submitting form:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        schemaId: schema.id,
        schema
      });
      const errorMessage = error.response?.data?.message || error.message || "Failed to save data. Please try again.";
      alert(`Error: ${errorMessage}`);
    }
  };

  const renderInput = (schemaId, input, fieldKey, value, error) => {
    switch (input.type) {
      case "select":
        return (
          <select
            className="input-field"
            value={value}
            onChange={(e) => handleInputChange(schemaId, input.key, e.target.value)}
            required={input.required}
            style={{
              width: "100%",
              textAlign: "center",
            }}
          >
            <option value="">Select</option>
            {input.options?.map((option, idx) => (
              <option key={idx} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case "boolean":
        return (
          <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
            <button
              type="button"
              className={`yes-no-btn yes-btn ${value === "true" ? "yes-btn-selected" : ""}`}
              onClick={() => handleInputChange(schemaId, input.key, "true")}
            >
              Yes
            </button>
            <button
              type="button"
              className={`yes-no-btn no-btn ${value === "false" ? "no-btn-selected" : ""}`}
              onClick={() => handleInputChange(schemaId, input.key, "false")}
            >
              No
            </button>
          </div>
        );

      case "date":
        return (
          <input
            type="date"
            className="input-field"
            value={value}
            onChange={(e) => handleInputChange(schemaId, input.key, e.target.value)}
            required={input.required}
            style={{
              fontSize: "11px",
            }}
          />
        );

      case "text":
        return (
          <input
            type="text"
            className="input-field"
            value={value}
            onChange={(e) => handleInputChange(schemaId, input.key, e.target.value)}
            placeholder="Enter value"
            required={input.required}
          />
        );

      case "number":
      default:
        return (
          <input
            type="number"
            step="any"
            className="input-field"
            value={value}
            onChange={(e) => handleInputChange(schemaId, input.key, e.target.value)}
            placeholder="Enter value"
            required={input.required}
            onWheel={(e) => e.target.blur()}
          />
        );
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (!schemas || schemas.length === 0) {
    return (
      <div style={{ width: "100%", padding: "20px", textAlign: "center" }}>
        <p style={{ color: "#5b5b5b", fontSize: "12px" }}>
          {section
            ? `No KPIs found for sector: ${sector}, section: ${section}`
            : `No KPIs found for sector: ${sector}`}
        </p>
      </div>
    );
  }

  const headings = ["Symbol", "Data Point", "Data Point Unit", "Value", "Assign"];

  return (
    <div style={{ width: "100%" }}>
      <div className="new-kpi-table-container">
        <div className="new-kpi-table">
          {/* Table Header */}
          <div className="new-kpi-table-header">
            <div className="new-kpi-table-row new-kpi-table-head-row">
              {headings.map((heading, index) => (
                <div
                  key={index}
                  className={`new-kpi-table-cell header-cell ${
                    index === 0 || index === 3 || index === 4 ? "centered" : ""
                  }`}
                >
                  <div className="new-kpi-table-header-content">{heading}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Table Body */}
          <div className="new-kpi-table-body">
            {schemas.map((schema) => {
              if (!schema.inputs || schema.inputs.length === 0) return null;

              const hasSubmitted = submitted[schema.id] || hasExistingSubmissions[schema.id];
              const inputCount = schema.inputs.length;

              return (
                <div key={schema.id} className="new-kpi-table-row new-kpi-table-body-row">
                  {/* Symbol - spans all inputs */}
                  <div className="new-kpi-table-cell centered">
                    <div className="new-kpi-table-cell-content" style={{ 
                      height: "100%", 
                      display: "flex", 
                      alignItems: "center", 
                      justifyContent: "center" 
                    }}>
                      {schema.code || ""}
                    </div>
                  </div>

                  {/* Data Points Column - one row per input */}
                  <div className="new-kpi-table-cell">
                    {schema.inputs.map((input, inputIdx) => (
                      <div key={inputIdx} className="new-kpi-table-cell-content data">
                        {input.label}
                        {input.required && <span style={{ color: "#ff0000", marginLeft: "4px" }}>*</span>}
                      </div>
                    ))}
                  </div>

                  {/* Data Point Unit Column - one row per input */}
                  <div className="new-kpi-table-cell">
                    {schema.inputs.map((input, inputIdx) => (
                      <div key={inputIdx} className="new-kpi-table-cell-content data">
                        {input.unit || schema.unit || "N/A"}
                      </div>
                    ))}
                  </div>

                  {/* Value Column - one row per input */}
                  <div className="new-kpi-table-cell centered">
                    {schema.inputs.map((input, inputIdx) => {
                      const fieldKey = `${schema.id}_${input.key}`;
                      const value = formData[fieldKey] || "";
                      const error = errors[fieldKey];
                      
                      return (
                        <div key={inputIdx} className="new-kpi-table-cell-content input-wrapper" style={{ 
                          flexDirection: "column", 
                          alignItems: "center" 
                        }}>
                          {renderInput(schema.id, input, fieldKey, value, error)}
                          {error && (
                            <div style={{ 
                              color: "#ff0000", 
                              fontSize: "10px", 
                              marginTop: "4px", 
                              width: "100%", 
                              textAlign: "center" 
                            }}>
                              {error}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Assign Column - spans all inputs */}
                  <div className="new-kpi-table-cell centered">
                    <div className="new-kpi-table-cell-content" style={{ 
                      height: "100%", 
                      display: "flex", 
                      alignItems: "center", 
                      justifyContent: "center" 
                    }}>
                      {(() => {
                        const { legacyCode, kpiName, kpiSection, assignMembers } =
                          getAssignContext(schema);
                        const assignedUsers =
                          allUsers?.filter((member) =>
                            assignMembers.includes(normalizeMemberId(member.id))
                          ) || [];

                        if (!legacyCode) {
                          return (
                            <AssignButton
                              text="Assign"
                              disabled
                              style={{ opacity: 0.5 }}
                            />
                          );
                        }

                        return assignMembers?.length > 0 ? (
                          <div className="messages-wrapper">
                            <div className="profile-images-container">
                              {assignedUsers?.slice(0, 2)?.map((img) => (
                                <img
                                  key={`${constant.IMG_URL}/${img.profileImage}`}
                                  src={
                                    img.profileImage
                                      ? `${constant.IMG_URL}/${img.profileImage}`
                                      : emptyuser
                                  }
                                  onError={(e) => {
                                    e.target.src = emptyuser;
                                  }}
                                  alt="Assigned"
                                  height={28}
                                  width={28}
                                />
                              ))}
                              {assignedUsers.length > 2 && (
                                <div className="remaining-count">
                                  +{assignedUsers.length - 2}
                                </div>
                              )}
                            </div>
                            <div
                              onClick={(e) =>
                                handleAssignClick(e, schema, assignMembers)
                              }
                              style={{ cursor: "pointer" }}
                              className="notifications-div"
                            >
                              <img
                                src={messages}
                                alt="Messaged"
                                height={24}
                                width={24}
                              />
                              {kpiSection?.unReadMessage > 0 && (
                                <span className="unread">
                                  {kpiSection.unReadMessage} Unread
                                </span>
                              )}
                            </div>
                          </div>
                        ) : (
                          <AssignButton
                            text="Assign"
                            onClick={(e) =>
                              handleAssignClick(e, schema, assignMembers || [])
                            }
                            style={{
                              backgroundColor: hasSubmitted ? "#e7ffe7" : undefined,
                              color: hasSubmitted ? "#329687" : undefined,
                            }}
                          />
                        );
                      })()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <AssignModal
        closeModal={closeModal}
        modalState={modalState}
        onAssignmentChange={handleAssignmentChange}
      />
    </div>
  );
};

export default DynamicKpiForm;

